/**
 * 模拟假进度条
 * @param options 配置选项
 * @returns 返回 { stop: () => void }，调用 stop() 时先停止普通模拟，然后启动冲刺到 100%
 */
export const simulateFakeProgress = ({
  duration = 60000, // 普通假进度模拟时长（毫秒）
  intervalTime = 150, // 进度更新间隔（毫秒）
  startFrom = 0, // 初始进度 [0 ~ 1]
  maxFakeProgress = 0.98, // ✅ 新增：普通假进度模拟阶段的最大进度（比如 0.99 表示 99%）
  onProgress, // 进度回调：({ percent }) => void
}: {
  duration?: number;
  intervalTime?: number;
  startFrom?: number; // [0 ~ 1]
  maxFakeProgress?: number; // ✅ 新增：普通阶段最大进度 [0 ~ 1]，默认 0.99
  onProgress: (state: { percent: number }) => void;
}) => {
  let currentProgress = Math.max(0, Math.min(startFrom, 1));
  const startTime = Date.now();

  // 缓动函数：先慢后快（用于冲刺阶段）
  const easeOutCubic = (t: number): number => {
    return 1 - Math.pow(1 - t, 3);
  };

  // 普通进度模拟定时器
  const timer = setInterval(() => {
    const elapsed = Date.now() - startTime;
    const raw = Math.min(elapsed / duration, 1);

    let target: number;

    if (raw <= 0.8) {
      const t = raw / 0.8;
      const easedT = t * t * (3 - 2 * t); // smooth step
      const randomFactor = 0.85 + Math.random() * 0.3;
      target = 0.8 * easedT * randomFactor;
    } else {
      const t = (raw - 0.8) / 0.2;
      const easedT = t * t * (3 - 2 * t);
      const randomFactor = 0.9 + Math.random() * 0.1;
      target = 0.8 + 0.19 * easedT * randomFactor;
    }

    // ✅ 新增：限制普通假进度最高只到 maxFakeProgress（比如 0.99）
    target = Math.min(target, maxFakeProgress);

    currentProgress = Math.max(currentProgress, target);
    currentProgress = Number(currentProgress.toFixed(3));
    onProgress({ percent: currentProgress });
  }, intervalTime);

  // stop() 方法：停止普通模拟，然后启动冲刺到 100%
  return {
    stop: () => {
      clearInterval(timer);

      // ✅ 启动冲刺到 100% 的逻辑
      const sprintStart = Date.now();
      const sprintDuration = 3000; // 3秒冲刺到 100%
      const startProgress = currentProgress;

      const sprintTimer = setInterval(() => {
        const elapsed = Date.now() - sprintStart;
        const raw = Math.min(elapsed / sprintDuration, 1);
        const eased = easeOutCubic(raw);

        let p = startProgress + (1 - startProgress) * eased;
        p = Math.min(p, 1.0); // 最终到 100%
        p = Math.max(p, currentProgress);

        currentProgress = Number(p.toFixed(3));
        onProgress({ percent: currentProgress });

        if (p >= 1.0) {
          clearInterval(sprintTimer);
        }
      }, intervalTime);
    },
  };
};
